import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { UploadQueue } from '../uploads/uploadQueue';

export const UploadQueueContext = createContext<UploadQueue | null>(null);

function useUploadQueue(): UploadQueue {
  const queue = useContext(UploadQueueContext);
  if (!queue) throw new Error('useRollUploads must be used within an UploadQueueProvider');
  return queue;
}

/** One roll's view of the queue, plus the commands that act on it. */
export function useRollUploads(orderId: string, rollId: string) {
  const queue = useUploadQueue();
  const snapshot = useSyncExternalStore(queue.subscribe, queue.getSnapshot);
  const jobs = useMemo(
    () => snapshot.jobs.filter((j) => j.orderId === orderId && j.rollId === rollId),
    [snapshot.jobs, orderId, rollId]
  );

  return {
    jobs,
    authPaused: snapshot.authPaused,
    add: useCallback((files: File[]) => queue.add(orderId, rollId, files), [queue, orderId, rollId]),
    start: useCallback(() => queue.start(orderId, rollId), [queue, orderId, rollId]),
    retryFailed: useCallback(() => queue.retryFailed(orderId, rollId), [queue, orderId, rollId]),
    remove: useCallback((jobId: string) => queue.remove(jobId), [queue]),
  };
}
